import React, {useEffect, useMemo, useRef, useState} from 'react';
import {PermissionEnum} from "../../../../utils/enums/permission-enum.js";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import {useQuery} from "@tanstack/react-query";
import DeviceService from "../../../../services/web/device-service.js";
import {useParams} from "react-router";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import { FaExternalLinkAlt } from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import clonesService from "../../../../services/web/clones-service.js";
import {toast} from "react-toastify";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import Swal from "sweetalert2";
import projectUtils from "../../../../utils/project-utils.js";
import deviceService from "../../../../services/web/device-service.js";
const BASENAME = import.meta.env.BASE_URL;

const DeviceForm = () => {
    const navigate = navigateHook();
    const {id} = useParams()
    const hasPermission = useHasPermission();
    const intervalRef = useRef(null);

    const hasUserPermission = hasPermission(PermissionEnum.USER)
    const [device, setDevice] = useState({})
    const [rules, setRules] = useState([])
    const [state, setState] = useState({})
    const [deviceOpt, setDeviceOpt] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    const gridRef = useRef(null);
    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);
    const columnDefs = [
        {headerName: "Nome da regra", field: "name", flex: 1, comparator: aggridUtils.nullLastComparator},
    ];

    const {fetchStatus, refetch} = useQuery({
        queryFn: () =>  DeviceService.getById(id).then(( data ) => {
            setDevice(data.device)
            setRules(data.rules)
            return data
        }),
        queryKey: ['device'],
        enabled: !!id
    });

    const {data: occurrenceId } = useQuery({
        queryFn: () =>  DeviceService.getOccurrenceId(id).then(({data} ) => {
            return [data[0].occurrenceid]
        }),
        queryKey: ['occurrenceId'],
        enabled: !!id
    });

    useQuery({
        queryFn: () =>  DeviceService.getMonitoramentoDevice(id).then(({data} ) => {
            const newData = []
            data.map(d => newData.push({ value: d.id, label: d.text }));

            setDeviceOpt(newData)
            return data
        }),
        queryKey: ['getMonitoramentoDevice'],
        enabled: !!id
    });

    useEffect(() => {
        if (id) {
            if (fetchStatus === 'idle') setIsLoading(false)
            else setIsLoading(true)
        }
    }, [fetchStatus]);

    const handleInputs = (id, value) => setState(prev => ({...prev, [id]: value}));

    function translateServerErrorMessage(message) {
        switch (message) {
            case 'not.authenticated':
                return 'Usuário sem permissão.';
            case 'thread.running':
                return 'Transferência em andamento.';
            default:
                return 'Ocorreu um erro ao transferir.';
        }
    }

    const switchMonitoringDevice = () => {
        deviceService.switchMonitoringDevice(id, state.device).then(() => {
            intervalRef.current = setInterval(async () => {
                await switchStatus(id, state.device);
            }, 1000);
        })
            .catch(error => {
                console.error(error);

                if (intervalRef.current) clearInterval(intervalRef.current);

                let message = translateServerErrorMessage(error.response.data.message);

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
    }

    const switchStatus = (id, target) => {
        deviceService.getMonitoringDeviceStatus(id, target)
            .then((data) => {
                if (data.message === "complete") {
                    if (intervalRef.current) clearInterval(intervalRef.current);

                    projectUtils.success();
                    refetch();
                } else {
                    setIsLoading(true)
                }
            })
            .catch(error => {
                console.error(error);
                setIsLoading(false)

                if (intervalRef.current) clearInterval(intervalRef.current);

                let message = translateServerErrorMessage(error.response.data.message);

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const transfer = () => {
        if (!state.device) return  projectUtils.swalErrorMessage('', "Selecione o dispositivo para a transferência de associações.", 'Ok');
        Swal.fire({
            title: "Transferir associações",
            icon: "warning",
            html: `<span class="font-weight-bold">De</span> ${device.username} - ${projectUtils.formatPhone(device.phone)} <br/> 
                   <span class="font-weight-bold">Para</span> ${deviceOpt.find(d => d.value === state?.device).label}<br />
                   Esta operação não pode ser desfeita!<br />
                   Para confirmar, digite <span class="font-bold">transferir</span> abaixo:
                   <span class="alert alert-warning d-none" id="alert-swal">Digite corretamente a palavra chave</span>`,
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true,
            input: "text",
            preConfirm(inputValue) {
                if (inputValue.trim() !== "transferir") {
                    Swal.showValidationMessage(`Digite corretamente a palavra-chave`);
                }
            }
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                switchMonitoringDevice()
            }
        });
    }

    const openInSentryView = () => {
        if (occurrenceId){
            const formData = new FormData();
            formData.append("occurrences", JSON.stringify(occurrenceId))

            clonesService.openAssociated(formData)
                .then(() => {
                    toast.success("A lista de ocorrências associadas será aberta no SentryView")
                })
                .catch(error => {
                    console.error(error);
                    toast.error('Ocorreu um erro ao abrir a ocorrência no SentryView.');
                });
        }else {
            toast.error('Ocorreu um erro ao abrir a ocorrência no SentryView.');
        }
    }

    const onCellClicked = ({ data }) => {
        const isBlank = false;
        const urlDev = `/rule-form/${data.ruleid}`;
        navigate(urlDev, isBlank);
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Associações
                </Template.Title>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Header>
                        <Card.Title>Agente</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="flex items-end gap-4 col-span-6">
                                <div className="flex-1">
                                    <InputForm id="name" value={device.username} label="Nome" disabled={true}/>
                                </div>

                                {
                                    hasUserPermission &&
                                        <Button onClick={() => window.open(`${BASENAME}user-agents/form/${ device.user_id }`)} target={"primary-outline"} title={"Usuário"}>
                                            <FaExternalLinkAlt />
                                        </Button>
                                }
                            </div>
                            <div className="col-span-3">
                                <InputForm id="type" value={device.type} label="Tipo" disabled={true}/>
                            </div>
                            <div className="col-span-3">
                                <InputForm id="phone" value={device.phone} label="Telefone" disabled={true} mask={MaskDefault.phone}/>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={"mt-2"}>
                    <Card.Header>
                        <Card.Title>{`Sentry - Ocorrências associadas ${ device.occurrences > 0 ? "- Total: " +  device.occurrences : "" }`}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {
                            device.occurrences > 0 ?
                                    <Button onClick={openInSentryView}>
                                        Abrir ocorrências associadas no Sentryview
                                    </Button>
                                :
                                    <div className="flex font-bold p-4 justify-center items-center rounded-sm w-[99%] h-[40px] mx-auto data-not-found text-[#1C6EAF] bg-[#A7D4FF]" >
                                        O dispositivo não está associado à nenhuma ocorrência.
                                    </div>
                        }

                    </Card.Body>
                </Card>

                <Card className={"mt-2"}>
                    <Card.Header>
                        <Card.Title>{`Sentry - Regras de alarme ${ rules.length > 0 ? "- Total: " + rules.length : "" }`}</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {
                            rules.length > 0 ?
                                <div className={"min-h-[74px] aggrid-row-pointer"}>
                                    <AgGridReact
                                        ref={gridRef}
                                        rowData={rules ?? []}
                                        columnDefs={columnDefs}
                                        animateRows={false}
                                        suppressCellFocus={true}
                                        gridOptions={{localeText: aggridUtils.localization()}}
                                        theme={theme}
                                        onCellClicked={onCellClicked}
                                        domLayout='autoHeight'
                                    />
                                </div>
                                :
                                <div className="flex font-bold p-4 justify-center items-center rounded-sm w-[99%] h-[40px] mx-auto data-not-found text-[#1C6EAF] bg-[#A7D4FF]" >
                                    O dispositivo não está associado à nenhuma regra.
                                </div>
                        }

                    </Card.Body>
                </Card>

                {
                    (rules.length > 0 || device.occurrences > 0) &&
                        <Card className={"mt-2"}>
                            <Card.Header>
                                <Card.Title>Transferência de associações</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="grid grid-cols-12 gap-4">
                                    <div className="col-span-12">
                                        <SelectComponent
                                            name="device"
                                            id="device"
                                            value={deviceOpt.find(d => d.value === state?.device)}
                                            options={deviceOpt}
                                            label={"Dispositivo de destino"}
                                            onChange={(selected) => handleInputs("device", selected?.value)}
                                        />
                                    </div>
                                    <div className="col-span-12">
                                        <Button onClick={transfer} actionFeedbackType="save" deferActionFeedback loadingText="Transferindo..." successText="Transferido com sucesso">
                                            Transferir associações
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                }
            </Template.Body>

        </Template>
    );
};

export default DeviceForm;